import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Button, Input, Popover, Select, Space, Tooltip, Typography, theme as antdTheme } from 'antd';
import type { InputRef } from 'antd';
import {
  CloseOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { space } from '@viq-admin/ui';
import type { BatchLead, LeadFieldsKey } from './types';
import type { LeadFieldStatus } from './useLeadRowSync';
import { INLINE_EDIT_COPY, LEAD_FIELD_LABEL, VISUALLY_HIDDEN, validateLeadField } from './inlineEdit';

/**
 * The Batch grid's inline-edit cells (spec 2026-09-17 §2.3, viq-admin#56),
 * generalised from `AssignmentCell`'s interaction, with the prototype's look:
 * "+ Add email" on a blank cell, a pencil on hover or focus.
 *
 * They only ever render when the shell passed `leadFieldsApi` and the table
 * is on its Active tab; otherwise `BatchTable` renders its plain read-only
 * cells exactly as before. They never save themselves: a commit hands the
 * trimmed, validated value to the table, which queues it on the sync store
 * (`useLeadRowSync`). `status` is that store's word on the field.
 */

/**
 * A DOM id for aria wiring. `useId`'s own (":r1:") is sanitised the way
 * MatchView does it: colons in an id break jsdom's selector engine, and a
 * plain id is simpler to reference from aria-describedby anyway.
 */
function useDomId(prefix: string): string {
  return `${prefix}-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

interface SyncIndicatorProps {
  status: LeadFieldStatus;
  /** "email for Jane Buyer" — completes the buttons' accessible names. */
  what: string;
  /** Id of the off-screen failure text; the cell's button is described by it. */
  descriptionId: string;
  onRetry: () => void;
  onDiscard: () => void;
}

/**
 * Saving: a small spinner (its tooltip says "Saving too fast, retrying…"
 * while a 429 is waited out). Failed: an error icon whose tooltip carries the
 * message, plus Retry and Discard as real buttons, so the failure is
 * reachable by keyboard and announced (the off-screen text describes the
 * cell's button), not only shown in a tooltip.
 */
export function CellSyncIndicator({ status, what, descriptionId, onRetry, onDiscard }: SyncIndicatorProps) {
  const { token } = antdTheme.useToken();
  if (status.kind === 'idle') return null;
  if (status.kind === 'saving') {
    const label =
      status.waiting === 'rate_limited'
        ? INLINE_EDIT_COPY.rateLimited
        : status.waiting === 'retrying'
          ? INLINE_EDIT_COPY.retrying
          : INLINE_EDIT_COPY.saving;
    return (
      <Tooltip title={label}>
        <LoadingOutlined
          role="img"
          aria-label={label}
          data-testid="inline-edit-saving"
          style={{ color: token.colorTextTertiary, flexShrink: 0 }}
        />
      </Tooltip>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      <span id={descriptionId} style={VISUALLY_HIDDEN}>
        {`Not saved: ${status.message}`}
      </span>
      <Tooltip title={status.message}>
        <ExclamationCircleFilled aria-hidden data-testid="inline-edit-failed" style={{ color: token.colorError }} />
      </Tooltip>
      <Tooltip title={INLINE_EDIT_COPY.retry}>
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          aria-label={`${INLINE_EDIT_COPY.retry} saving ${what}`}
          onClick={onRetry}
        />
      </Tooltip>
      <Tooltip title={INLINE_EDIT_COPY.discard}>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label={`${INLINE_EDIT_COPY.discard} unsaved ${what}`}
          onClick={onDiscard}
        />
      </Tooltip>
    </span>
  );
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Enter / Space activate a role="button" span, as they would a button. */
function activateOnKey(e: React.KeyboardEvent, activate: () => void) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    activate();
  }
}

export interface EditableTextCellProps {
  field: Extract<LeadFieldsKey, 'company' | 'title' | 'email' | 'phone'>;
  /** The value the row shows (the sync store's overlay already applied). */
  value: string | null | undefined;
  /** The row's display name, for accessible names ("Edit email for Jane Buyer"). */
  leadName: string;
  status: LeadFieldStatus;
  /**
   * Replaces the field's own rule (`validateLeadField`) on the trimmed value:
   * the message to show, or null when valid. The CRM Clean tab's email cell
   * passes the CRM engine's stricter shape.
   */
  validate?: (value: string) => string | null;
  /** A trimmed, validated value that differs from `value` (or re-sends a failed one). */
  onSave: (value: string) => void;
  onRetry: () => void;
  onDiscard: () => void;
}

/**
 * Company / Title / Email / Phone. Click or Enter opens an antd `Input`
 * prefilled with the current value. Enter or blur saves when the trimmed value
 * changed; Esc cancels; Tab saves and lets focus move on to the next editable
 * cell in the row (they are the row's next tab stops). An invalid value stays
 * in edit mode with its message and is never sent.
 */
export function EditableTextCell({
  field,
  value,
  leadName,
  status,
  validate,
  onSave,
  onRetry,
  onDiscard,
}: EditableTextCellProps) {
  const { token } = antdTheme.useToken();
  const label = LEAD_FIELD_LABEL[field];
  const shown = value ?? '';
  const blank = shown.trim() === '';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  // True once this edit has been committed or cancelled, so the blur that
  // follows Enter / Tab / Esc (or the input unmounting) can't commit again.
  const closedRef = useRef(true);
  const refocusRef = useRef(false);
  const displayRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<InputRef>(null);
  const errorId = useDomId('viq-inline-error');
  const failedId = useDomId('viq-inline-failed');

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus({ cursor: 'end' });
    } else if (refocusRef.current) {
      refocusRef.current = false;
      displayRef.current?.focus();
    }
  }, [editing]);

  function open() {
    setDraft(shown);
    setError(null);
    closedRef.current = false;
    setEditing(true);
  }

  function close(refocus: boolean) {
    closedRef.current = true;
    refocusRef.current = refocus;
    setError(null);
    setEditing(false);
  }

  /** Queue the draft if it changed. False = invalid: stay open and say why. */
  function commit(): boolean {
    const next = draft.trim();
    // Unchanged is a no-op — unless the value on screen is a FAILED one, in
    // which case saving it again is the operator asking to retry.
    if (next === shown.trim() && status.kind !== 'failed') return true;
    const problem = validate ? validate(next) : validateLeadField(field, next);
    if (problem) {
      setError(problem);
      return false;
    }
    onSave(next);
    return true;
  }

  if (editing) {
    return (
      <Tooltip
        open={!!error}
        title={
          error ? (
            <span id={errorId} role="alert">
              {error}
            </span>
          ) : null
        }
        color={token.colorError}
        placement="bottom"
      >
        <Input
          ref={inputRef}
          size="small"
          value={draft}
          autoComplete="off"
          aria-label={`${capitalise(label)} for ${leadName}`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          status={error ? 'error' : undefined}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (commit()) close(true);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              close(true);
            } else if (e.key === 'Tab') {
              // Valid: saved now, and the browser moves focus on to the next
              // tab stop (the next editable cell); the blur then closes this
              // editor. Invalid: keep focus here with the message.
              if (commit()) closedRef.current = true;
              else e.preventDefault();
            }
          }}
          onBlur={() => {
            if (closedRef.current) {
              setError(null);
              setEditing(false);
              return;
            }
            if (commit()) close(false);
          }}
        />
      </Tooltip>
    );
  }

  // `undefined` means this backend didn't send the field at all — nothing is
  // known, so no "+ Add" prompt claims it is missing (the dash, as today).
  const content =
    value === undefined ? (
      <Typography.Text type="secondary">—</Typography.Text>
    ) : blank ? (
      <span style={{ color: token.colorLink, whiteSpace: 'nowrap' }}>
        <PlusOutlined aria-hidden style={{ marginInlineEnd: space.xs }} />
        {INLINE_EDIT_COPY.addPrompt(label)}
      </span>
    ) : (
      <span title={shown} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {shown}
      </span>
    );

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, minWidth: 0 }}>
      <span
        ref={displayRef}
        role="button"
        tabIndex={0}
        className="viq-inline-edit"
        aria-label={
          value === undefined
            ? `Edit ${label} for ${leadName}`
            : blank
              ? `${INLINE_EDIT_COPY.addPrompt(label)} for ${leadName}`
              : `Edit ${label} for ${leadName}: ${shown}`
        }
        aria-describedby={status.kind === 'failed' ? failedId : undefined}
        onClick={open}
        onKeyDown={(e) => activateOnKey(e, open)}
        style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', cursor: 'text' }}
      >
        {content}
        {!blank && (
          <EditOutlined aria-hidden className="viq-inline-edit-pencil" style={{ color: token.colorTextTertiary }} />
        )}
      </span>
      <CellSyncIndicator
        status={status}
        what={`${label} for ${leadName}`}
        descriptionId={failedId}
        onRetry={onRetry}
        onDiscard={onDiscard}
      />
    </span>
  );
}

export interface EditableSelectCellProps {
  value: string | null | undefined;
  leadName: string;
  status: LeadFieldStatus;
  /** Renders a set value exactly as the read-only Temp cell does (the pill). */
  renderValue: (value: string) => ReactNode;
  onSave: (value: string) => void;
  onRetry: () => void;
  onDiscard: () => void;
}

/**
 * Temp: a `Select` of blank / hot / warm / cold that saves on change. A blank
 * cell reads "+ Set".
 */
export function EditableSelectCell({
  value,
  leadName,
  status,
  renderValue,
  onSave,
  onRetry,
  onDiscard,
}: EditableSelectCellProps) {
  const { token } = antdTheme.useToken();
  const current = value ?? '';
  const [editing, setEditing] = useState(false);
  const refocusRef = useRef(false);
  const displayRef = useRef<HTMLSpanElement>(null);
  const failedId = useDomId('viq-inline-failed');
  const label = LEAD_FIELD_LABEL.temperature;

  useEffect(() => {
    if (!editing && refocusRef.current) {
      refocusRef.current = false;
      displayRef.current?.focus();
    }
  }, [editing]);

  function close(refocus: boolean) {
    refocusRef.current = refocus;
    setEditing(false);
  }

  if (editing) {
    return (
      <Select
        size="small"
        autoFocus
        defaultOpen
        virtual={false}
        value={current}
        aria-label={`${capitalise(label)} for ${leadName}`}
        popupMatchSelectWidth={false}
        style={{ width: '100%' }}
        options={[
          { value: '', label: INLINE_EDIT_COPY.temperatureNone },
          { value: 'hot', label: 'hot' },
          { value: 'warm', label: 'warm' },
          { value: 'cold', label: 'cold' },
        ]}
        onChange={(next: string) => {
          if (next !== current || status.kind === 'failed') onSave(next);
          close(true);
        }}
        onBlur={() => close(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close(true);
        }}
      />
    );
  }

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: space.xs, minWidth: 0 }}>
      <span
        ref={displayRef}
        role="button"
        tabIndex={0}
        className="viq-inline-edit"
        aria-label={
          current
            ? `Edit ${label} for ${leadName}: ${current}`
            : `${INLINE_EDIT_COPY.setTemperature} ${label} for ${leadName}`
        }
        aria-describedby={status.kind === 'failed' ? failedId : undefined}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => activateOnKey(e, () => setEditing(true))}
        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
      >
        {current ? (
          renderValue(current)
        ) : (
          <span style={{ color: token.colorLink, whiteSpace: 'nowrap' }}>
            <PlusOutlined aria-hidden style={{ marginInlineEnd: space.xs }} />
            {INLINE_EDIT_COPY.setTemperature}
          </span>
        )}
      </span>
      <CellSyncIndicator
        status={status}
        what={`${label} for ${leadName}`}
        descriptionId={failedId}
        onRetry={onRetry}
        onDiscard={onDiscard}
      />
    </span>
  );
}

export interface NameEditorProps {
  /** The row as shown (overlay applied) — its stored first / last prefill. */
  row: Pick<BatchLead, 'firstName' | 'lastName'>;
  leadName: string;
  /** The name's sync state (first / last together). */
  status: LeadFieldStatus;
  /** Exactly `{ firstName, lastName }`, trimmed. Never a display name. */
  onSave: (patch: { firstName: string; lastName: string }) => void;
  onRetry: () => void;
  onDiscard: () => void;
}

/**
 * The Name cell's editor: a pencil (shown on row hover or focus) that opens a
 * First / Last popover (D2). The name link itself still opens the drawer. The
 * popover sends both parts; the server derives the display name from them.
 * The parts come from the row, verbatim (`''` is a stored blank); the grid
 * never splits a display name (plan D2). It only shows when the rows carry
 * first / last at all (`rowsCarryLeadFields`).
 */
export function NameEditor({ row, leadName, status, onSave, onRetry, onDiscard }: NameEditorProps) {
  const { token } = antdTheme.useToken();
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [error, setError] = useState<{ field: 'firstName' | 'lastName'; message: string } | null>(null);
  const pencilRef = useRef<HTMLButtonElement>(null);
  const firstRef = useRef<InputRef>(null);
  // Focus First once the popover's content is in the page (it mounts with
  // the popup, after this render), so `autoFocus` alone can't be relied on.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => firstRef.current?.focus({ cursor: 'end' }), 0);
    return () => clearTimeout(t);
  }, [open]);
  const firstId = useDomId('viq-inline-first');
  const lastId = useDomId('viq-inline-last');
  const errorId = useDomId('viq-inline-error');
  const failedId = useDomId('viq-inline-failed');

  /** The stored parts, as the popover opens with them. */
  function stored() {
    return { first: row.firstName ?? '', last: row.lastName ?? '' };
  }

  function show() {
    const p = stored();
    setFirst(p.first);
    setLast(p.last);
    setError(null);
    setOpen(true);
  }

  function hide(refocus: boolean) {
    setOpen(false);
    setError(null);
    if (refocus) pencilRef.current?.focus();
  }

  function save() {
    const f = first.trim();
    const l = last.trim();
    for (const [field, v] of [
      ['firstName', f],
      ['lastName', l],
    ] as const) {
      const problem = validateLeadField(field, v);
      if (problem) {
        setError({ field, message: problem });
        return;
      }
    }
    const before = stored();
    const unchanged = f === before.first.trim() && l === before.last.trim();
    if (!unchanged || status.kind === 'failed') onSave({ firstName: f, lastName: l });
    hide(true);
  }

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
    marginBottom: space.xs,
  };
  const describedBy = (field: 'firstName' | 'lastName') => (error?.field === field ? errorId : undefined);

  const form = (
    <div role="dialog" aria-label={`Edit name for ${leadName}`}>
      <form
        style={{ width: 240 }}
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            hide(true);
          }
        }}
      >
        <label htmlFor={firstId} style={labelStyle}>
          {INLINE_EDIT_COPY.firstName}
        </label>
        <Input
          id={firstId}
          ref={firstRef}
          size="small"
          autoComplete="off"
          value={first}
          status={error?.field === 'firstName' ? 'error' : undefined}
          aria-invalid={error?.field === 'firstName' ? true : undefined}
          aria-describedby={describedBy('firstName')}
          onChange={(e) => {
            setFirst(e.target.value);
            setError(null);
          }}
          style={{ marginBottom: space.sm }}
        />
        <label htmlFor={lastId} style={labelStyle}>
          {INLINE_EDIT_COPY.lastName}
        </label>
        <Input
          id={lastId}
          size="small"
          autoComplete="off"
          value={last}
          status={error?.field === 'lastName' ? 'error' : undefined}
          aria-invalid={error?.field === 'lastName' ? true : undefined}
          aria-describedby={describedBy('lastName')}
          onChange={(e) => {
            setLast(e.target.value);
            setError(null);
          }}
        />
        {error && (
          <Typography.Text type="danger" id={errorId} role="alert" style={{ display: 'block', marginTop: space.xs }}>
            {error.message}
          </Typography.Text>
        )}
        <Space style={{ marginTop: space.sm, width: '100%', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => hide(true)}>
            {INLINE_EDIT_COPY.cancel}
          </Button>
          <Button size="small" type="primary" htmlType="submit">
            {INLINE_EDIT_COPY.save}
          </Button>
        </Space>
      </form>
    </div>
  );

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
      <Popover
        open={open}
        // Opened by the pencil; an outside click dismisses without saving.
        onOpenChange={(next) => (next ? show() : hide(false))}
        trigger="click"
        placement="bottomLeft"
        content={form}
        destroyOnHidden
      >
        <Button
          ref={pencilRef}
          type="text"
          size="small"
          icon={<EditOutlined />}
          className="viq-name-pencil"
          aria-label={`Edit name for ${leadName}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-describedby={status.kind === 'failed' ? failedId : undefined}
          style={{ color: token.colorTextTertiary }}
        />
      </Popover>
      <CellSyncIndicator
        status={status}
        what={`name for ${leadName}`}
        descriptionId={failedId}
        onRetry={onRetry}
        onDiscard={onDiscard}
      />
    </span>
  );
}
